import React from "react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "../../ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { ChevronLeft } from "lucide-react";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { cn } from "../../../lib/utils";

export const DESKTOP_QUERY = "(min-width: 1024px)";

/**
 * Where a detail setting opens: a bottom sheet on phones (thumb reach — the
 * Done button lands where Generate sits) and a small dialog on desktop.
 * Changes apply as they are made; Done only closes.
 *
 * `footer` replaces Done for sheets whose job ends in an action (Start,
 * Build…); `description` shows as a visible subtitle when `showDescription`.
 * `onBack` adds a Back button to the header, for a sheet opened from a list
 * in another sheet.
 * `fill` opens the sheet at full height, for content that grows after it opens
 * (search results). vaul pins a phone sheet's height when the keyboard comes
 * up, so a sheet that opens short stays short and its results scroll in a
 * sliver above Done.
 */
const DetailSheet = ({
  open,
  onOpenChange,
  title,
  description,
  showDescription = false,
  footer,
  onBack,
  fill = false,
  children,
}) => {
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const back = onBack && (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onBack}
      className="pointer-events-auto absolute left-2 top-1/2 h-8 -translate-y-1/2 gap-0.5 px-2 text-primary hover:bg-transparent hover:text-primary active:opacity-60"
    >
      <ChevronLeft className="h-4 w-4" />
      Back
    </Button>
  );
  const done = footer || (
    <Button
      type="button"
      variant="outline"
      className="h-12 w-full border-border bg-muted/40 text-base font-semibold hover:bg-muted/60"
      onClick={() => onOpenChange(false)}
    >
      Done
    </Button>
  );

  if (desktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={cn("flex max-h-[80dvh] max-w-md flex-col gap-0 p-0", fill && "h-[80dvh]")}>
          <div className="relative">
            <DialogTitle className={cn("px-5 pb-2 pt-5 text-base", onBack && "pl-20 pr-20 text-center")}>
              {title}
            </DialogTitle>
            {back && <div className="pointer-events-none absolute inset-x-0 bottom-2 top-5">{back}</div>}
          </div>
          <DialogDescription
            className={
              showDescription && description
                ? "-mt-1 px-5 pb-3 text-sm text-muted-foreground"
                : "sr-only"
            }
          >
            {description || title}
          </DialogDescription>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pb-5">
            {children}
          </div>
          <div className="border-t border-border p-4">{done}</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className={cn(fill && "h-[85dvh]")}>
        <div className="relative">
          <DrawerTitle className={cn("px-4 pb-3 pt-1", onBack && "px-20")}>{title}</DrawerTitle>
          {back && <div className="pointer-events-none absolute inset-x-0 bottom-3 top-1">{back}</div>}
        </div>
        <DrawerDescription
          className={
            showDescription && description
              ? "-mt-2 px-4 pb-3 text-center text-sm text-muted-foreground"
              : "sr-only"
          }
        >
          {description || title}
        </DrawerDescription>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 pb-4">
          {children}
        </div>
        <div className="border-t border-border px-4 pb-4 pt-3">{done}</div>
      </DrawerContent>
    </Drawer>
  );
};

export default DetailSheet;
